/* eslint-disable no-useless-escape */
import * as Yup from 'yup';
import { maxValidationNumber, maxValidationString, minValidationNumber, minValidationString, postalCodeErrorMessage, requiredField, validateFileSize, validateSingleFileSize } from '../../helperFuncs';
import { matchIsValidTel } from 'mui-tel-input';
import UserService from '../../../services/UserService';
import { IntExt } from '../../permissions';
import i18n from '../../../i18n';

export const validationSchema = (disable_file_validation: boolean) => {
  
  return Yup.object({
    // step 1
    title_property:
      Yup
        .string()
        .required(requiredField)
        .min(1, minValidationString(1)).max(40, maxValidationString(40)),
    category_lvl1: Yup
      .array()
      .required(requiredField)
      .min(1, requiredField),
    category_loc: Yup
      .array(),
    ...(i18n.language !== 'en' && 
    {
      info_property: Yup
        .string()
        .required(requiredField)
        .min(1, minValidationString(1))
        .max(1200, maxValidationString(1200)) 
    }),
    ...(UserService.hasRole([IntExt.External]) && 
    i18n.language === 'en' &&
    {
      info_property_en: Yup.string()
        .required(requiredField)
        .min(1, minValidationString(1))
        .max(1200, maxValidationString(1200))
    }),
    ...(i18n.language !== 'en' && 
      {
        limitations: Yup.string()
          .min(1, minValidationString(1))
          .max(200, maxValidationString(200))
      }),
    limitations_en: Yup.string()
      .min(1, minValidationString(1))
      .max(200, maxValidationString(200)),
    ...(UserService.hasRole([IntExt.Internal]) && i18n.language !== 'en' && {
      licensing: Yup
        .string()
        .min(1, minValidationString(1))
        .max(500, maxValidationString(500))
    }),
    ...(UserService.hasRole([IntExt.Internal]) && {
      licensing_en: Yup
        .string()
        .min(1, minValidationString(1))
        .max(500, maxValidationString(500))
    }),
    // step 2
    marker: Yup.string().required(requiredField).nullable(),
    region: Yup.number().required(requiredField).nullable(),
    addressName: Yup.string()
      .required(requiredField)
      .nullable()
      .min(1, minValidationString(1))
      .max(50, maxValidationString(50)),
    addressNumber: Yup.string()
      .nullable()
      .min(1, minValidationNumber(1))
      .max(15, maxValidationNumber(15))
      .matches(/^\d+([A-Za-z]|\-\d+)?$/, 'Μη αποδεκτή μορφή Αριθμού Διεύθυνσης'),
    addressPostalCode: Yup.number()
      .required(requiredField)
      .min(10000, postalCodeErrorMessage())
      .max(99999, postalCodeErrorMessage()),
    // step 3
    ...(disable_file_validation
      ? {
        fileThumbnail: Yup
          .mixed()
          .required(requiredField)
          .test('fileSizeThumbnail', 'Παρακαλώ ανεβάστε τουλάχιστον ένα αρχείο', (value) => {
            if (Array.isArray(value)) return value.length > 0;
            return true;
          }),
      }
      : {
        fileThumbnail: Yup
          .mixed()
          .required(requiredField)
          .test('fileSizeThumbnail', 'Παρακαλώ ανεβάστε αρχείο μέχρι 5MB', (value) => {
            return validateSingleFileSize(value, 5);
          })
          .test('fileSizeThumbnail', 'Παρακαλώ ανεβάστε τουλάχιστον ένα αρχείο', (value) => {
            if (Array.isArray(value)) return value.length > 0;
            return true;
          })
        ,
      }
    ),
    ...(disable_file_validation
      ? {
        otherMedia: Yup
          .mixed()
          .nullable()
      }
      : {
        otherMedia: Yup.mixed()
          .nullable()
          .test('fileSizeMedia', 'Παρακαλώ ανεβάστε αρχείο/α μέχρι 15MB', (value) => validateFileSize(value, 15))
          .test('fileSizeMedia', 'Παρακαλώ ανεβάστε μέχρι 10 αρχεία', (value) => {
            if (!value) return true;
            return value?.length <= 10;
          })
      }
    )
    ,
    ...(i18n.language !== 'en' && 
    {
      keywords_gr: Yup.array()
        .test('keywords_gr', 'Εισάγετε λέξεις - κλειδιά μόνο στην ελληνική γλώσσα', (value) => {
          if (!Array.isArray(value)) {
            return true; 
          }
          const allValid = value.every((item) => /^[Α-Ωα-ωίϊΐόάέύϋΰήώ]+/g.test(item));
          return allValid;
        })
        .min(1, minValidationString(1))

    }),

    keywords: Yup.array()
      .test('keywords', 'Εισάγετε λέξεις - κλειδιά μόνο στην αγγλική γλώσσα', (value) => {
        if (!Array.isArray(value)) {
          return true; 
        }
        const allValid = value.every((item) => /^[a-zA-Z\s]+$/.test(item));
        return allValid;
      })
      .min(1, minValidationString(1)
      ),
    // step 4
    ...(UserService.hasRole([IntExt.External]) &&
    {
      name: Yup.string().required(requiredField).min(3, minValidationString(3))
        .max(40, maxValidationString(40))
  
    }),
    ...(UserService.hasRole([IntExt.External]) &&
    {
      surname: Yup.string().required(requiredField).min(3, minValidationString(3))
        .max(40, maxValidationString(40))
  
    }),
    phone: Yup.string()
      .required(requiredField)
      .test('phone', 'Παρακαλώ προσθέστε έγκυρη μορφή τηλεφώνου', (value) => matchIsValidTel(value || '')),
    email: Yup.string().required(requiredField).email('Μη αποδεκτή μορφή Email')
      .matches(/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/, 'Μη αποδεκτή μορφή Email'),
    // step 5
    attachments: Yup.mixed().nullable().test('fileSizeDoc', 'Παρακαλώ ανεβάστε αρχείο/α μέχρι 4MB', (value) => validateFileSize(value)),
  });
};

export const validationSchemaPreview = (disable_file_validation: boolean) => {
  return Yup.object({
    // step 1
    title: Yup
      .string()
      .required(requiredField)
      .min(1, minValidationString(1))
      .max(40, maxValidationString(40)),
    location_category_level_2: Yup.array(),
    location_category_level_1: Yup.array().required(requiredField).min(1, requiredField + 'Πρέπει να επιλέξετε Θεματική Ενότητα'),
    ...(i18n.language !== 'en' && {
      info: Yup.string()
        .nullable()
        .required(requiredField)
        .min(1, minValidationString(1))
        .max(1200, maxValidationString(1200))
    }),
    ...(UserService.hasRole([IntExt.External]) && 
    i18n.language === 'en' && {
      info_en: Yup.string()
        .required(requiredField)
        .min(1, minValidationString(1))
        .max(1200, maxValidationString(1200))
    }),
    ...(i18n.language !== 'en' && {
      restrictions: Yup
        .string()
        .min(1, minValidationString(1))
        .max(200, maxValidationString(200))
    }),
    restrictions_en: Yup
      .string()
      .nullable()
      .min(1, minValidationString(1))
      .max(200, maxValidationString(200)),
    ...(i18n.language !== 'en' && {
      tags_gr: Yup.array(Yup.string().nullable().min(1, minValidationString(1))
        .max(50, maxValidationString(50, 'για κάθε Λέξη - Κλειδί')))
    }),
    tags: Yup.array(Yup.string().min(1, minValidationString(1))
      .max(50, maxValidationString(50, 'για κάθε Λέξη - Κλειδί'))),
    // step 2
    show_on_map:
      Yup.string()
        .required(requiredField)
        .nullable(),
    region_id:
      Yup.number()
        .required(requiredField)
        .nullable(),
    address: Yup
      .string().nullable()
      .min(1, minValidationString(1))
      .max(50, maxValidationString(50)),
    address_number: Yup
      .string()
      .nullable()
      .min(1, minValidationNumber(1))
      .max(5, maxValidationNumber(5))
      .matches(/^\d+([A-Za-z]|\-\d+)?$/, 'Μη αποδεκτή μορφή Αριθμού Διεύθυνσης'),
    tk: Yup
      .number()
      .required(requiredField)
      .typeError(requiredField)
      .min(10000, postalCodeErrorMessage())
      .max(99999, postalCodeErrorMessage()),
    // step 3
    ...(disable_file_validation
      ? {
        otherMedia: Yup
          .mixed()
          .nullable()
      }
      : {
        otherMedia: Yup.mixed()
          .nullable()
          .test('fileSizeMedia', 'Παρακαλώ ανεβάστε αρχείο/α μέχρι 15MB', (value) => validateFileSize(value, 15))
          .test('fileSizeMedia', 'Παρακαλώ ανεβάστε μέχρι 10 αρχεία', (value) => {
            if (!value) return true;
            return value?.length <= 10;
          })
      }
    ),
    // )
    // thumbnail: Yup.mixed()
    //   .required(requiredField)
    //   .test('fileSizeThumbnail', 'Παρακαλώ ανεβάστε αρχείο μέχρι 5MB', (value) => validateSingleFileSize(value, 5)),
    // media: Yup.mixed()
    //   .nullable()
    //   .test('fileSizeMedia', 'Παρακαλώ ανεβάστε αρχείο/α μέχρι 15MB', (value) => validateFileSize(value, 15))
    //   .test('fileSizeMedia', 'Παρακαλώ ανεβάστε μέχρι 10 αρχεία', (value) => {
    //     if (!value) return true;
    //     return value?.length <= 10;
    //   }),
    // step 4
    ...(UserService.hasRole([IntExt.External]) &&
    {
      name: Yup.string().required(requiredField).min(3, minValidationString(3))
        .max(40, maxValidationString(40))
  
    }),
    ...(UserService.hasRole([IntExt.External]) &&
    {
      lastname:
        Yup.string()
          .required(requiredField)
          .min(3, minValidationString(3))
          .max(40, maxValidationString(40))
    
    }),
    mobile: Yup.string()
      .required(requiredField)
      .test('phone', 'Παρακαλώ προσθέστε έγκυρη μορφή τηλεφώνου', (value) => matchIsValidTel(value || '')),
    email: Yup.string().required(requiredField).email('Μη αποδεκτή μορφή Email')
      .matches(/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/, 'Μη αποδεκτή μορφή Email'),
    // step 5
    attachments: Yup.mixed().nullable().test('fileSizeDoc', 'Παρακαλώ ανεβάστε αρχείο/α μέχρι 4MB', (value) => validateFileSize(value)),
  });
};
